/**
 * Reports Page
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchProjects } from '@/store/projectSlice';
import { timelogAPI } from '@/api/timelogAPI';
import { TimeLogChart } from '@/components/TimeLogChart';
import { TimeLogSummary } from '@/types';
import { BarChart3, Calendar } from 'lucide-react';
import dayjs from 'dayjs';
import Share from '@/components/Share';

export const Reports = () => {
  const dispatch = useAppDispatch();
  const { projects } = useAppSelector((state) => state.projects);

  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [timeSummary, setTimeSummary] = useState<TimeLogSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  // Share uses shared component; no local state needed

  useEffect(() => {
    dispatch(fetchProjects({ is_active: true,organization_id:user?.organization_id }));
  }, [dispatch]);

  const loadTimeSummary = useCallback(async () => {
    if (!selectedProject) {
      setTimeSummary([]);
      return;
    }

    setLoading(true);
    try {
      const summary = await timelogAPI.getProjectTimeSummary(
        selectedProject,
        dayjs(startDate).startOf('day').toISOString(),
        dayjs(endDate).endOf('day').toISOString()
      );
      setTimeSummary(summary);
    } catch (error) {
      console.error('Failed to load time summary:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, startDate, endDate]);

  useEffect(() => {
    if (!selectedProject && projects.length > 0) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject]);

  useEffect(() => {
    loadTimeSummary();
  }, [loadTimeSummary]);

  // (Share logic handled by Share component)

  const totalHours = useMemo(
    () => timeSummary.reduce((sum, item) => sum + item.total_hours, 0),
    [timeSummary]
  );
  const totalTasks = useMemo(
    () => timeSummary.reduce((sum, item) => sum + item.task_count, 0),
    [timeSummary]
  );
  const memberCount = useMemo(() => timeSummary.length, [timeSummary]);

  // Copy link handled by Share component

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600 mt-1">Time tracking and project analytics</p>
          </div>
        </div>
        <div className="flex items-center gap-2 relative"><Share label="Share" className="btn btn-secondary btn-sm flex items-center gap-2" subject="Shared view: Reports" /></div>
      </div>
      {/* Shared Share component handles popover */}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value || null)}
            className="input"
          >
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input"
          />
        </div>
      </div>

      {selectedProject ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Hours</p>
                  <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(1)}h</p>
                </div>
                <BarChart3 className="w-10 h-10 text-primary-600" />
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tasks Worked</p>
                  <p className="text-2xl font-bold text-gray-900">{totalTasks}</p>
                </div>
                <BarChart3 className="w-10 h-10 text-green-600" />
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Team Members</p>
                  <p className="text-2xl font-bold text-gray-900">{memberCount}</p>
                </div>
                <BarChart3 className="w-10 h-10 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Chart */}
          {loading ? (
            <div className="text-center py-12">Loading report...</div>
          ) : (
            <TimeLogChart data={timeSummary} />
          )}

          {/* Detail Table */}
          <div className="card mt-6">
            <h3 className="text-lg font-semibold mb-4">Detailed Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Member
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                      Hours
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                      Tasks
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                      Avg Hours/Task
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {timeSummary.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-sm text-gray-900">{item.user_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 text-right">
                        {item.total_hours.toFixed(1)}h
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 text-right">
                        {item.task_count}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 text-right">
                        {item.task_count > 0 ? (item.total_hours / item.task_count).toFixed(1) : '0.0'}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card text-center py-12 text-gray-500">
          Please select a project to view reports
        </div>
      )}
    </div>
  );
};

